/**
 * Legal structures available by country for fund domiciliation
 * Based on common European and international fund structures
 */

interface LegalStructureOption {
  value: string;
  label: string;
  description?: string;
}

// Legal structures by country
const LEGAL_STRUCTURES_BY_COUNTRY: Record<string, LegalStructureOption[]> = {
  // Netherlands
  NLD: [
    {
      value: 'cv',
      label: 'CV (Commanditaire Vennootschap)',
      description: 'Limited Partnership',
    },
    {
      value: 'bv',
      label: 'BV (Besloten Vennootschap)',
      description: 'Private Limited Company',
    },
    {
      value: 'nv',
      label: 'NV (Naamloze Vennootschap)',
      description: 'Public Limited Company',
    },
    {
      value: 'fgr',
      label: 'FGR (Fonds voor Gemene Rekening)',
      description: 'Fund for Joint Account',
    },
    { value: 'cooperatief', label: 'Coöperatief', description: 'Cooperative' },
  ],

  // Luxembourg
  LUX: [
    {
      value: 'scsp',
      label: 'SCSp',
      description: 'Special Limited Partnership',
    },
    { value: 'scs', label: 'SCS', description: 'Limited Partnership' },
    { value: 'sarl', label: 'SARL', description: 'Private Limited Company' },
    { value: 'sa', label: 'SA', description: 'Public Limited Company' },
    {
      value: 'sicav',
      label: 'SICAV',
      description: 'Investment Company with Variable Capital',
    },
    {
      value: 'sicar',
      label: 'SICAR',
      description: 'Investment Company in Risk Capital',
    },
    { value: 'fcp', label: 'FCP', description: 'Common Investment Fund' },
    {
      value: 'raif',
      label: 'RAIF',
      description: 'Reserved Alternative Investment Fund',
    },
  ],

  // United Kingdom
  GBR: [
    {
      value: 'limited_partnership',
      label: 'Limited Partnership',
      description: 'LP',
    },
    {
      value: 'llp',
      label: 'LLP',
      description: 'Limited Liability Partnership',
    },
    {
      value: 'ltd',
      label: 'Limited Company',
      description: 'Private Limited Company',
    },
    { value: 'plc', label: 'PLC', description: 'Public Limited Company' },
    {
      value: 'oeic',
      label: 'OEIC',
      description: 'Open-Ended Investment Company',
    },
    {
      value: 'icvc',
      label: 'ICVC',
      description: 'Investment Company with Variable Capital',
    },
    { value: 'unit_trust', label: 'Unit Trust', description: 'Unit Trust' },
  ],

  // Germany
  DEU: [
    {
      value: 'kg',
      label: 'KG (Kommanditgesellschaft)',
      description: 'Limited Partnership',
    },
    { value: 'gmbh', label: 'GmbH', description: 'Limited Liability Company' },
    {
      value: 'gmbh_co_kg',
      label: 'GmbH & Co. KG',
      description: 'Limited Partnership with GmbH as GP',
    },
    {
      value: 'ag',
      label: 'AG (Aktiengesellschaft)',
      description: 'Stock Corporation',
    },
    {
      value: 'investment_kg',
      label: 'Investment-KG',
      description: 'Investment Limited Partnership',
    },
  ],

  // France
  FRA: [
    {
      value: 'sca',
      label: 'SCA',
      description: 'Limited Partnership with Shares',
    },
    {
      value: 'sas',
      label: 'SAS',
      description: 'Simplified Joint Stock Company',
    },
    { value: 'sarl', label: 'SARL', description: 'Limited Liability Company' },
    { value: 'sa', label: 'SA', description: 'Public Limited Company' },
    { value: 'fcp', label: 'FCP', description: 'Common Investment Fund' },
    {
      value: 'sicav',
      label: 'SICAV',
      description: 'Investment Company with Variable Capital',
    },
    {
      value: 'scpi',
      label: 'SCPI',
      description: 'Real Estate Investment Company',
    },
  ],

  // Ireland
  IRL: [
    {
      value: 'limited_partnership',
      label: 'Limited Partnership',
      description: 'LP',
    },
    {
      value: 'icav',
      label: 'ICAV',
      description: 'Irish Collective Asset-management Vehicle',
    },
    { value: 'plc', label: 'PLC', description: 'Public Limited Company' },
    {
      value: 'ltd',
      label: 'Limited Company',
      description: 'Private Limited Company',
    },
    { value: 'unit_trust', label: 'Unit Trust', description: 'Unit Trust' },
    { value: 'ccf', label: 'CCF', description: 'Common Contractual Fund' },
    {
      value: 'investment_lp',
      label: 'Investment Limited Partnership',
      description: 'ILP',
    },
  ],

  // United States
  USA: [
    {
      value: 'delaware_lp',
      label: 'Delaware LP',
      description: 'Delaware Limited Partnership',
    },
    {
      value: 'delaware_llc',
      label: 'Delaware LLC',
      description: 'Delaware Limited Liability Company',
    },
    {
      value: 'delaware_corp',
      label: 'Delaware Corporation',
      description: 'Delaware C-Corp',
    },
    {
      value: 'cayman_lp',
      label: 'Cayman LP',
      description: 'Cayman Limited Partnership (US Feeder)',
    },
  ],

  // Switzerland
  CHE: [
    { value: 'lp', label: 'LP', description: 'Limited Partnership' },
    {
      value: 'sarl',
      label: 'Sàrl/GmbH',
      description: 'Limited Liability Company',
    },
    { value: 'sa', label: 'SA/AG', description: 'Stock Corporation' },
    {
      value: 'sicav',
      label: 'SICAV',
      description: 'Investment Company with Variable Capital',
    },
    {
      value: 'sicaf',
      label: 'SICAF',
      description: 'Investment Company with Fixed Capital',
    },
  ],

  // Singapore
  SGP: [
    {
      value: 'limited_partnership',
      label: 'Limited Partnership',
      description: 'LP',
    },
    {
      value: 'llp',
      label: 'LLP',
      description: 'Limited Liability Partnership',
    },
    {
      value: 'private_limited',
      label: 'Private Limited',
      description: 'Pte Ltd',
    },
    { value: 'vcc', label: 'VCC', description: 'Variable Capital Company' },
  ],

  // Hong Kong
  HKG: [
    {
      value: 'limited_partnership',
      label: 'Limited Partnership',
      description: 'LP',
    },
    {
      value: 'private_limited',
      label: 'Private Limited',
      description: 'Limited Company',
    },
    { value: 'ofc', label: 'OFC', description: 'Open-ended Fund Company' },
  ],

  // Cayman Islands
  CYM: [
    { value: 'elp', label: 'ELP', description: 'Exempted Limited Partnership' },
    { value: 'llc', label: 'LLC', description: 'Limited Liability Company' },
    { value: 'spc', label: 'SPC', description: 'Segregated Portfolio Company' },
    {
      value: 'exempted_company',
      label: 'Exempted Company',
      description: 'Exempted Company',
    },
  ],

  // Jersey
  JEY: [
    {
      value: 'limited_partnership',
      label: 'Limited Partnership',
      description: 'LP',
    },
    {
      value: 'llp',
      label: 'LLP',
      description: 'Limited Liability Partnership',
    },
    {
      value: 'incorporated_lp',
      label: 'Incorporated LP',
      description: 'Incorporated Limited Partnership',
    },
    {
      value: 'separate_lp',
      label: 'Separate LP',
      description: 'Separate Limited Partnership',
    },
    { value: 'private_fund', label: 'JPF', description: 'Jersey Private Fund' },
  ],

  // Guernsey
  GGY: [
    {
      value: 'limited_partnership',
      label: 'Limited Partnership',
      description: 'LP',
    },
    { value: 'pcc', label: 'PCC', description: 'Protected Cell Company' },
    { value: 'icc', label: 'ICC', description: 'Incorporated Cell Company' },
    {
      value: 'private_fund',
      label: 'GPF',
      description: 'Guernsey Private Fund',
    },
  ],
};

// Helper function to get legal structures for a country
export function getLegalStructuresForCountry(
  countryCode: string
): LegalStructureOption[] {
  return LEGAL_STRUCTURES_BY_COUNTRY[countryCode] || [];
}
